import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { CalendarData, Calendar_onValueChangeEvent, Dates } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import {
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  FilterService,
  FilterKind,
} from 'app/modules/collection/services/filter.service';
import { ComparisonFilterSet } from 'app/providers/bridge-api/dashboard.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { FilterHandler } from '../comparison-filter-item.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-comparison-filter-date-item',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './comparison-filter-date-item.component.html',
  styleUrls: ['./comparison-filter-date-item.component.scss'],
})
export class ComparisonFilterDateItemComponent
  implements
    FilterHandler<{ fixedRange: CalendarData; relativeRange: string }, Dates | any[]>,
    OnChanges
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() field: string;
  @Input() filterKind: FilterKind;
  @Input() filters;
  @Input() isEnabled;

  @Output() filtersChange = new EventEmitter();

  displayValue: {
    fixedRange: CalendarData;
    relativeRange: string;
  };
  calendarMode = 'fixed';
  readonly maxDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

  constructor(
    private timezoneService: TimezoneService,
    private filterService: FilterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters) {
      this.displayValue = this.fromFilter(changes.filters.currentValue);
    }
  }

  fromFilter = (filter: ComparisonFilterSet) => {
    const ret = {
      fixedRange: {
        initialDateRange: {
          first: null,
          last: null,
        },
        preselectedCalendarListOptions: this.filterService.generateRelativeRangeOptions(),
      },
      relativeRange: null,
    };

    if (!this.hasFilterValue(filter)) {
      this.calendarMode = 'fixed';

      return ret;
    }

    const range = filter.and[0].value.split('/');
    ret.relativeRange = filter.and[0].key;
    this.calendarMode = 'relative';

    if (filter.and[0].key === 'daterange') {
      ret.relativeRange = null;
      ret.fixedRange.initialDateRange.first = new Date(range[1]);
      ret.fixedRange.initialDateRange.last = new Date(range[2]);
      this.calendarMode = 'fixed';
    }

    if (!this.field) {
      // we don't need to iterate every operatior the fieldName is in the and[0].analyticItem
      this.field = filter.and[0].analyticItem;
    }

    return ret;
  };

  toFilter = (value: any) => {
    const firstDate = this.timezoneService
      .padToStart(value.dates.first)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    const lastDate = this.timezoneService
      .padToEnd(value.dates.last)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    let filterValue = `daterange/${firstDate}/${lastDate}`;
    let filterKey = 'daterange';

    if (value.optionId) {
      filterValue = value.optionId;
      filterKey = value.optionId;
    }

    return {
      and: [
        {
          analyticItem: this.field,
          value: filterValue,
          key: filterKey,
          kind: this.filterKind,
        },
      ],
      or: [],
      not: [],
    };
  };

  hasFilterValue(filterSet: ComparisonFilterSet) {
    return !!filterSet?.and.length;
  }

  onFilterValueSet($event) {
    const calData: Calendar_onValueChangeEvent = $event.detail;
    if (!this.isEnabled) {
      return;
    }

    this.displayValue = {
      fixedRange: {
        initialDateRange: calData.dates,
        preselectedCalendarListOptions: this.displayValue.fixedRange.preselectedCalendarListOptions,
      },
      relativeRange: calData.optionId,
    };

    this.filtersChange.emit(this.toFilter(calData));
  }
}
